<template>
  <div>
    <div class="nestedInnerItem">
      <cvt-select
        :value="borderStyle"
        mode="dropdown"
        :background-mode="mode"
        :floating-label="false"
        :label="$t('elements.properties.border.style')"
        @input="(value) => onBorderStyleChange(value)"
      >
        <option
          v-for="style in borderStyles"
          :key="style"
          :value="style"
          v-text="style"
        />
      </cvt-select>
      <div style="margin: 5px" />
      <template v-if="themeManagerVersion > 1">
        <cvt-select
          :value="borderColor"
          mode="dropdown"
          :background-mode="mode"
          :floating-label="false"
          :label="$t('elements.properties.border.color')"
          @input="(value) => onBorderColorChange(value)"
        >
          <option
            v-for="(colorData, colorKey) in themeColors"
            :key="colorKey"
            :value="colorKey"
          >
            <span class="color-option">
              <span
                class="color-swatch"
                :style="{ backgroundColor: getRgbString(colorData) }"
              ></span>
              <span class="color-label">{{ colorKey }}</span>
            </span>
          </option>
        </cvt-select>
      </template>
      <template v-else>
        <cvt-input
          v-model="borderColorComputed"
          style="width: max-content"
          type="text"
          :clearable="false"
          :label="$t('elements.properties.border.color')"
        />
      </template>
    </div>

    <div class="divider mt-2" />

    <div v-if="showBorderPosition" class="row">
      <div class="col-md-12 d-flex align-items-center text-capitalize">
        {{ $t('elements.properties.border.position') }}
      </div>
    </div>

    <div v-if="showBorderPosition" class="row">
      <div class="col">
        <cvt-radio-group
          :key="borderPositionKey"
          orientation="horizontal"
          :initial-value="positionDefVal"
        >
          <cvt-radio-button
            :value="pv.all"
            :disabled="disableAllBorder"
            type="multiselect"
            @radioClick="($event) => updatePosition($event, 'all')"
          >
            <cvt-icon icon="border-all"></cvt-icon>
          </cvt-radio-button>
          <cvt-radio-button
            :value="pv.top"
            type="multiselect"
            @radioClick="($event) => updatePosition($event, 'top')"
          >
            <cvt-icon icon="border-top"></cvt-icon>
          </cvt-radio-button>
          <cvt-radio-button
            :value="pv.btm"
            type="multiselect"
            @radioClick="($event) => updatePosition($event, 'btm')"
          >
            <cvt-icon icon="border-bottom"></cvt-icon>
          </cvt-radio-button>
          <cvt-radio-button
            :value="pv.lft"
            type="multiselect"
            @radioClick="($event) => updatePosition($event, 'lft')"
          >
            <cvt-icon icon="border-left"></cvt-icon>
          </cvt-radio-button>
          <cvt-radio-button
            :value="pv.ryt"
            type="multiselect"
            @radioClick="($event) => updatePosition($event, 'ryt')"
          >
            <cvt-icon icon="border-right"></cvt-icon>
          </cvt-radio-button>
        </cvt-radio-group>
      </div>
    </div>

    <div v-if="showBorderPosition" class="divider mt-2" />

    <div class="row">
      <div class="col-md-12 mb-3 d-flex align-items-center text-capitalize">
        {{ $t('elements.properties.border.width') }}
      </div>
      <div class="col-12">
        <cvt-slider
          id="slider-border-width"
          :tooltips-enabled="true"
          :step-marks-enabled="true"
          class="slider"
          :min="minBorderWidth"
          :max="maxBorderWidth"
          :value="borderWidth"
          :step="1"
          @change="onBorderWidthChange"
        />
      </div>
    </div>

    <div v-if="showBorderRadius" class="divider mt-2" />

    <div v-if="showBorderRadius" class="row">
      <div class="col-md-12 mb-3 d-flex align-items-center text-capitalize">
        {{ $t('elements.properties.border.radius') }}
      </div>
      <div class="col-12">
        <cvt-slider
          id="slider-border-radius"
          :tooltips-enabled="true"
          :step-marks-enabled="true"
          class="slider"
          :min="minBorderRadius"
          :max="maxBorderRadius"
          :value="borderRadius"
          :step="5"
          @change="onBorderRadiusChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import * as _ from 'lodash'
import { mapGetters } from 'vuex'
import * as getters from '@/js/store/modules/getters'
export default {
  name: 'BorderStyleOptions',
  props: {
    mode: {
      type: String,
      default: 'LIGHT',
    },
    borderStyle: {
      type: String,
      default: 'none',
    },
    borderColor: {
      type: String,
      default: '',
    },
    borderWidth: {
      type: Number,
      default: 0,
    },
    borderRadius: {
      type: Number,
      default: 0,
    },
    updatePropFunction: {
      type: Function,
      required: true,
    },
    showBorderRadius: {
      type: Boolean,
      default: true,
    },
    minBorderWidth: {
      type: Number,
      default: 1,
    },
    maxBorderWidth: {
      type: Number,
      default: 10,
    },
    minBorderRadius: {
      type: Number,
      default: 0,
    },
    maxBorderRadius: {
      type: Number,
      default: 100,
    },
    showBorderPosition: {
      type: Boolean,
      default: false,
    },
    borderPosition: {
      type: Object,
      default: () => ({
        all: 'set',
        top: '',
        btm: '',
        lft: '',
        ryt: '',
      }),
    },
  },
  data() {
    return {
      borderStyles: ['none', 'solid', 'dashed', 'dotted', 'double', 'groove'],
      positionDefVal: 'set',
      pv: {
        all: 'set',
        top: '',
        btm: '',
        lft: '',
        ryt: '',
      },
      borderPositionKey: 1,
    }
  },
  computed: {
    ...mapGetters({
      themeColors: getters.THEME_GET_COLORS,
      themeManagerVersion: getters.THEME_MANAGER_VERSION,
    }),
    borderColorComputed: {
      get() {
        return this.borderColor
      },
      set: _.throttle(function ({ target }) {
        this.updatePropFunction('borderColor', target.value)
      }, 300),
    },
    disableAllBorder() {
      return (
        this.pv.top !== '' ||
        this.pv.btm !== '' ||
        this.pv.lft !== '' ||
        this.pv.ryt !== ''
      )
    },
  },
  created() {
    if (this.borderPosition && Object.keys(this.borderPosition).length) {
      this.pv = { ...this.borderPosition }
    }
    this.setDefaultAllBorder()
  },
  methods: {
    onBorderStyleChange: _.throttle(function (value) {
      this.updatePropFunction('borderStyle', value)
    }, 300),
    onBorderColorChange: _.throttle(function (value) {
      this.updatePropFunction('borderColor', value)
    }, 300),
    getRgbString(colorData) {
      if (!colorData || !colorData.color) return 'transparent'
      const [r, g, b] = colorData.color
      const alpha = colorData.valpha !== undefined ? colorData.valpha : 1
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    onBorderWidthChange: _.throttle(function (value) {
      this.updatePropFunction('borderWidth', value)
    }, 300),
    onBorderRadiusChange: _.throttle(function (value) {
      this.updatePropFunction('borderRadius', value)
    }, 300),
    setDefaultAllBorder() {
      if (
        this.pv.top !== '' ||
        this.pv.btm !== '' ||
        this.pv.lft !== '' ||
        this.pv.ryt !== ''
      ) {
        this.pv.all = ''
      } else {
        this.pv.all = this.positionDefVal
      }
      this.updatePropFunction('borderPosition', this.pv)
    },
    updatePosition(key, from) {
      if (key === '') {
        this.pv[from] = this.positionDefVal
      } else {
        this.pv[from] = ''
      }
      this.setDefaultAllBorder()
      this.borderPositionKey++
    },
  },
}
</script>

<style scoped>
.color-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-swatch {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.color-label {
  text-transform: capitalize;
}
</style>
